import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Loader2, User } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useActiveContributors } from "@/hooks/useActiveContributors";

interface ActiveContributorsListProps {
  channelName?: string;
}

const ActiveContributorsList = ({ channelName }: ActiveContributorsListProps) => {
  const navigate = useNavigate();
  const { data: contributors, isLoading } = useActiveContributors(channelName);

  if (isLoading) {
    return (
      <Card className="border-2 border-black">
        <CardContent className="p-8 flex justify-center items-center">
          <Loader2 className="h-8 w-8 animate-spin" />
        </CardContent>
      </Card>
    );
  }

  if (!contributors || contributors.length === 0) {
    return (
      <Card className="border-2 border-black">
        <CardHeader className="pb-2 border-b border-gray-200">
          <CardTitle className="text-lg">Active Contributors</CardTitle>
        </CardHeader>
        <CardContent className="p-6">
          <p className="text-gray-500 text-center">No active contributors found</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-2 border-black">
      <CardHeader className="pb-2 border-b border-gray-200">
        <CardTitle className="text-lg">Active Contributors</CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        <div className="divide-y divide-gray-200">
          {contributors.map((contributor) => (
            <div
              key={contributor.id}
              onClick={() => {
                if (contributor.username) {
                  navigate(`/myspai-profile/${contributor.username}`);
                }
              }}
              className="p-4 hover:bg-gray-50 transition-colors cursor-pointer flex items-center gap-4"
            >
              {/* Avatar */}
              <Avatar className="h-12 w-12 shrink-0">
                <AvatarImage 
                  src={contributor.avatar_url || '/placeholder.svg'} 
                  alt={contributor.display_name}
                />
                <AvatarFallback>
                  <User className="h-6 w-6" />
                </AvatarFallback>
              </Avatar>

              {/* Name and Title */}
              <div className="flex-1 min-w-0">
                <h4 className="font-semibold text-base text-gray-900 truncate">
                  {contributor.display_name}
                </h4>
                {contributor.title && (
                  <p className="text-sm text-gray-600 truncate">
                    {contributor.title}
                  </p>
                )}
              </div>

              {/* Channels and KB Count */}
              <div className="flex items-center gap-2 shrink-0 flex-wrap">
                {contributor.channels.map((channel) => (
                  <Badge key={channel} variant="outline" className="text-xs">
                    {channel}
                  </Badge>
                ))}
                <Badge className="bg-primary text-white text-xs">
                  {contributor.kb_count} KB{contributor.kb_count !== 1 ? 's' : ''}
                </Badge>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

export default ActiveContributorsList;
